import {
  AdConfigType,
  HighlightAttributes,
} from '@scentregroup/shared/graphql/__generated__/graphql-codegen'
import { IIssue } from './marketplace-product'
import { ExcludeDeep } from './utility'

export interface SGPageTemplateProductPromotion {
  campaignId: string
  title: string
}

export interface SGPageTemplateWithAdZones {
  title: string
  metaTitle?: string
  shortDescription?: string | null
  modules: SGPageTemplateModuleWithAdZones[]
}

export type SGPageTemplate = ExcludeDeep<
  SGPageTemplateWithAdZones,
  SGPageTemplateAdZone
>

export type SGPageTemplateModuleWithAdZones =
  | SGPageTemplateBasicContent
  | SGPageTemplateContentCollectionWithAdZones
  | SGPageTemplateProductCollection
  | SGPageTemplateDynamicContent

export type SGPageTemplateModule = ExcludeDeep<
  SGPageTemplateModuleWithAdZones,
  SGPageTemplateAdZone
>

export type SGPageTemplateAdZone = {
  __typename: 'SGPageTemplateAdZone'
  id: string
}

export type SGPageTemplateBasicContent = {
  __typename: 'SGPageTemplateBasicContent'
  title?: string | null
  shortDescription?: string | null
  bodyCopy?: string | null
  callsToAction: SGPageTemplateCallToAction[]
} & {
  /**
   * indicates the Basic Content title should be rendered as an H2 instead of an H1
   *
   * note: not part of Contentful schema
   */
  titleAs?: 'h2'
}

export type SGPageTemplateContentCollectionWithAdZones = {
  __typename: 'SGPageTemplateContentCollection'
  kind: SGPageTemplateContentCollectionKind
  title?: string | null
  shortDescription?: string | null
  items: (SGPageTemplateContentEntry | SGPageTemplateAdZone)[]
  callToAction?: SGPageTemplateCallToAction | null
  analyticsMetadata?: SGPageTemplateDisplayAnalyticsMetadata
}

export type SGPageTemplateContentCollection = ExcludeDeep<
  SGPageTemplateContentCollectionWithAdZones,
  SGPageTemplateAdZone
>

export type SGPageTemplateProductCollection = {
  __typename: 'SGPageTemplateProductCollection'
  kind: SGPageTemplateProductCollectionKind
  title: string
  shortDescription?: string
  url?: string | null
  image?: SGPageTemplateImage
  items: SGPageTemplateProduct[]
  analyticsMetadata?: SGPageTemplateDisplayAnalyticsMetadata
}

export type SGPageTemplateDisplayAnalyticsMetadata = {
  correlationIds: string[]
  displayId: string
  position: string | null
}

export type SGPageTemplateContentCollectionKind =
  | 'BANNER'
  | 'CATEGORY_GRID'
  | 'COLOUR_CARDS'
  | 'FEATURE'
  | 'INLINE_DECK'
  | 'STORE_GRID'
  | 'UTILITY_PANEL'
  | 'SPOTLIGHT'
  | 'WHATS_HAPPENING_DECK'

export type SGPageTemplateProductCollectionKind =
  | 'COLLECTION_DECK'
  | 'PRODUCT_REEL'

export type SGPageTemplateContentAnalyticsMetadata = {
  correlationId: string
  promotion: {
    creative: string | null
  }
  title: string
}

export type ContentOrchestrationAnalyticsMetadataStandard = {
  __typename: 'ContentOrchestrationAnalyticsMetadataStandard'
  displayId: string
  moduleSlug: string | null
}

export type ContentOrchestrationAnalyticsMetadataPersonalised = {
  __typename: 'ContentOrchestrationAnalyticsMetadataPersonalised'
  displayId: string
  moduleSlug: string | null
  primaryRank: number
  secondaryRank: number
  methodUsed: string
  knownScgId: boolean
}

export type ContentOrchestrationAnalyticsMetadata =
  | ContentOrchestrationAnalyticsMetadataStandard
  | ContentOrchestrationAnalyticsMetadataPersonalised

export type SGPageTemplateContentEntry = {
  __typename: 'SGPageTemplateContentEntry'
  adConfig?: AdConfigType | null
  kind: string
  title: string
  image: SGPageTemplateImage
  secondaryImage?: SGPageTemplateImage | null
  shortDescription?: string | null
  callToAction: SGPageTemplateCallToAction
  analyticsMetadata?: SGPageTemplateContentAnalyticsMetadata
  orchestrationMetadata?: ContentOrchestrationAnalyticsMetadata
  supplementaryText?: string | null
  memberExclusive?: boolean
  highlightAttribute?: HighlightAttributes | null
}

export type SGPageTemplateProduct = {
  __typename: 'SGPageTemplateProduct'
  shop: SGPageTemplateProductShop
  variantGroupCode: string
  kind: string
  sku: string
  title: string
  image: {
    url: string
    alt?: string | null
  }
  price: string | null
  percentDiscount?: string | null
  discountedPrice?: string | null
  brand?: string | null
  callToAction: {
    url: string
  }
  issues?: IIssue[]
  category?: string[] | null
  objectID?: string | null
  promotions?: SGPageTemplateProductPromotion[]
}

interface SGPageTemplateProductShop {
  id: number
  name?: string | null
  retailer?: SGPageTemplateProductRetailer | null
}

interface SGPageTemplateProductRetailer {
  sys: {
    id: string
  }
}

export type SGPageTemplateCallToAction = {
  label: string
  url: string
}

export type SGPageTemplateImage = {
  type: SGPageTemplateImageType
  url: string
  alt?: string | null
  supportingColour?: string | null
  cinemagraphUrl?: string | null
}

export const sgPageTemplateImageTypeValues = ['SQUARE', 'WIDE'] as const
export type SGPageTemplateImageType =
  typeof sgPageTemplateImageTypeValues[number]

export type SGPageTemplateDynamicContent = {
  __typename: 'SGPageTemplateDynamicContent'
  kind: string
}
